import React, { useState, useEffect } from 'react';
import {
  RotateCcw,
  Plus,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  X,
  CreditCard,
  ShieldAlert
} from 'lucide-react';
import { AdminRefundItem } from '../../../types/admin.types';
import {
  fetchAdminRefunds,
  createAdminRefundApi,
} from '../../../lib/adminService';

interface RefundsTabProps {
  onShowNotice: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export const RefundsTab: React.FC<RefundsTabProps> = ({ onShowNotice }) => {
  const [refunds, setRefunds] = useState<AdminRefundItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // New refund modal
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadRefunds = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminRefunds();
      setRefunds(data);
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to load refunds', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRefunds();
  }, []);

  const handleCreateRefund = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentId || !amount || !reason.trim()) {
      onShowNotice('Payment ID, refund amount, and reason are required', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await createAdminRefundApi(paymentId, Number(amount), reason);
      onShowNotice(`Refund of ₹${amount} issued successfully.`, 'success');
      setModalOpen(false);
      setPaymentId('');
      setAmount('');
      setReason('');
      loadRefunds();
    } catch (err: any) {
      onShowNotice(err.message || 'Error processing refund', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white tracking-tight">Refund Management</h2>
            <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono text-xs font-bold">
              {refunds.length} Refunds Processed
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Section 23: Complete audit trail, reason validation, and gateway response tracking for client refunds.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="px-3.5 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 cursor-pointer shadow-lg shadow-purple-950"
          >
            <Plus className="w-4 h-4" />
            <span>Issue Refund</span>
          </button>

          <button
            type="button"
            onClick={loadRefunds}
            className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-purple-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Refunds Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-950/60 text-slate-400 font-mono uppercase text-[10px] border-b border-slate-800">
              <tr>
                <th className="py-3 px-4">Refund ID</th>
                <th className="py-3 px-4">Payment ID</th>
                <th className="py-3 px-4">Client</th>
                <th className="py-3 px-4">Amount (₹)</th>
                <th className="py-3 px-4">Mandatory Reason</th>
                <th className="py-3 px-4">Authorized Admin</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {refunds.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-500">
                    No refunds issued yet.
                  </td>
                </tr>
              ) : (
                refunds.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-3.5 px-4 font-mono font-bold text-rose-400">{r.id}</td>
                    <td className="py-3.5 px-4 font-mono text-slate-300">{r.paymentId}</td>
                    <td className="py-3.5 px-4 font-bold text-white">{r.clientName}</td>
                    <td className="py-3.5 px-4 font-mono font-bold text-rose-300">
                      ₹{r.amount.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3.5 px-4 text-slate-300 max-w-xs truncate">{r.reason}</td>
                    <td className="py-3.5 px-4 font-mono text-slate-400 text-[11px]">{r.adminEmail}</td>
                    <td className="py-3.5 px-4">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border bg-emerald-500/15 text-emerald-400 border-emerald-500/30">
                        {r.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right text-slate-400 font-mono text-[11px]">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Issue Refund Modal (Section 23) */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <RotateCcw className="w-5 h-5 text-purple-400" />
                <span>Issue Customer Refund</span>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateRefund} className="space-y-4 text-xs">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">Payment ID: *</label>
                <input
                  type="text"
                  required
                  value={paymentId}
                  onChange={(e) => setPaymentId(e.target.value)}
                  placeholder="e.g. PAY-8952"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:border-purple-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">Refund Amount (₹): *</label>
                <input
                  type="number"
                  required
                  min={1}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 500"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:border-purple-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">
                  Mandatory Audit Reason: *
                </label>
                <textarea
                  rows={3}
                  required
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Detail the exact reason for granting this customer refund..."
                  className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:border-purple-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-bold cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? 'Processing...' : 'Authorize Refund'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
